import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useGetSalesOverviewQuery } from 'Services/Admin/DashboardApiSlice';
import { useGetConfigurationByPlantQuery } from 'Services/Admin/ConfigurationApiSlice';

interface SalesOverviewProps {
  fromDate: string;
  toDate: string;
  warehouse: string;
  categoryId: string;
  productId: string;
  mockMultiplier?: number;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short' });
};

export const SalesOverview = ({ 
  fromDate: propFromDate, 
  toDate: propToDate, 
  warehouse, 
  categoryId, 
  productId 
}: SalesOverviewProps) => {
  const plant = process.env.REACT_APP_PLANT || '';
  const { data: config } = useGetConfigurationByPlantQuery(plant);
  const { data: overviewData, isLoading, isFetching } = useGetSalesOverviewQuery({
    plant, fromDate: propFromDate, toDate: propToDate, warehouse, categoryId, productId
  }, { pollingInterval: 60000 });

  const dec = config?.numberOfDecimal ? parseInt(config.numberOfDecimal) : 2;
  const results = overviewData?.results || {};
  
  // Transform API data for the chart with precise index-based alignment
  const currentTrend = results.currentTrend || [];
  const previousTrend = results.previousTrend || [];
  
  const chartData = (currentTrend || []).map((item: any, idx: number) => ({
    name: item.date ? new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '',
    currentRevenue: item.revenue,
    previousRevenue: previousTrend?.[idx]?.revenue || 0,
    currentActualDate: item.actualDate,
    previousActualDate: previousTrend?.[idx]?.actualDate || 'N/A'
  }));

  // Custom Tooltip to show detailed actual dates and exact revenue values
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-100 shadow-xl rounded-xl">
          <div className="mb-3 pb-2 border-b border-gray-50">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Selected Range</p>
            <p className="text-[13px] font-black text-[#1B2559]">{data.currentActualDate}</p>
            <p className="text-[14px] font-black text-blue-600">{formatCurrency(data.currentRevenue)}</p>
          </div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Previous Range</p>
            <p className="text-[13px] font-black text-[#1B2559]">{data.previousActualDate}</p>
            <p className="text-[14px] font-black text-gray-500">{formatCurrency(data.previousRevenue)}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Helper to format currency
  const formatCurrency = (val: any) => {
    const num = Number(val) || 0;
    // Show full value in tooltip or when small, but can keep L/Cr for Axis if needed
    // The user asked for "exact number in the UI" earlier, so we use full formatting
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
  };

  // Axis formatter for the Y axis to keep it clean
  const formatYAxis = (val: any) => {
    if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="border-b border-gray-100 px-6 py-4 flex justify-between items-center bg-white shrink-0">
        <h3 className="text-[16px] font-bold text-[#1B2559]">Sales Overview</h3>
        <span className="text-[10px] text-gray-400 font-bold uppercase bg-gray-50 px-2 py-1 rounded">
          {propFromDate} to {propToDate}
        </span>
      </div>
      
      <div className="p-6 flex-1 flex flex-col min-h-0">
        <div className="flex justify-between items-center mb-4 shrink-0 px-2">
          <h4 className="text-[14px] font-bold text-[#1B2559]">Revenue Trend</h4>
          <div className="flex gap-4 text-xs font-semibold text-gray-500">
            <span className="flex items-center gap-2"><div className="w-4 h-1.5 rounded-full bg-[#1E5EFF]"></div> Selected Range</span>
            <span className="flex items-center gap-2"><div className="w-4 h-1.5 rounded-full bg-[#CBD5E1]"></div> Previous Range</span>
          </div>
        </div>
        
        <div className="flex-1 w-full min-h-[240px] xl:min-h-[280px] mb-2">
          {isLoading || isFetching ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-10 h-10 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: -10, bottom: 45 }}>
                <defs>
                  <linearGradient id="colorTY" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1E5EFF" stopOpacity={0.06}/>
                    <stop offset="95%" stopColor="#1E5EFF" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#FAFAFA" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{fill: '#94A3B8', fontSize: 10, fontWeight: 700}} 
                  dy={30} 
                  interval="preserveStart" 
                  minTickGap={60} 
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{fill: '#94A3B8', fontSize: 10, fontWeight: 700}} 
                  tickFormatter={formatYAxis}
                />
                <Tooltip content={<CustomTooltip />} />
                
                <Area 
                  type="monotone" 
                  dataKey="previousRevenue" 
                  name="Previous Range"
                  stroke="#CBD5E1" 
                  strokeWidth={2} 
                  fill="none" 
                  isAnimationActive={false}
                  strokeDasharray="0"
                  dot={chartData.length < 15 ? { r: 3, fill: '#fff', stroke: '#CBD5E1', strokeWidth: 2 } : false}
                  activeDot={{ r: 4, strokeWidth: 0, fill: "#CBD5E1" }}
                  connectNulls
                />
                <Area 
                  type="monotone" 
                  dataKey="currentRevenue" 
                  name="Selected Range"
                  stroke="#1E5EFF" 
                  strokeWidth={2} 
                  isAnimationActive={false}
                  fillOpacity={1} 
                  fill="url(#colorTY)" 
                  dot={chartData.length < 15 ? { r: 3, fill: '#fff', stroke: '#1E5EFF', strokeWidth: 2 } : false}
                  activeDot={{ r: 4, strokeWidth: 0, fill: "#1E5EFF" }}
                  connectNulls
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 mt-auto border-t border-gray-50">
          <div className="bg-[#F8FAFC] rounded-lg p-5 group hover:bg-white transition-all cursor-default">
            <h4 className="text-[22px] font-black text-[#1B2559] tracking-tight leading-none group-hover:scale-105 transition-transform origin-left">
              {isLoading || isFetching ? "..." : formatCurrency(results.totalRevenue)}
            </h4>
            <p className="text-[12px] font-semibold text-[#1B2559] mt-2 tracking-tight">Total Revenue</p>
          </div>
          <div className="bg-[#F8FAFC] rounded-lg p-5 group hover:bg-white transition-all cursor-default">
            <h4 className="text-[22px] font-black text-[#1B2559] tracking-tight leading-none group-hover:scale-105 transition-transform origin-left">
              {isLoading || isFetching ? "..." : `${results.profitMargin || 0}%`}
            </h4>
            <p className="text-[12px] font-semibold text-[#1B2559] mt-2 tracking-tight">Profit Margin</p>
          </div>
          <div className="bg-white rounded-lg p-5 border border-gray-100 flex flex-col justify-center group hover:shadow-sm transition-all cursor-default">
             <p className="text-[11px] font-semibold text-[#1B2559] mb-2 tracking-tight leading-none">Top Product</p>
            <h4 className="text-[13px] font-black text-[#1B2559] whitespace-normal leading-tight tracking-tight group-hover:text-blue-600 transition-colors" title={results.topProduct || "No Data"}>
              {isLoading || isFetching ? "..." : (results.topProduct || "N/A")}
            </h4>
          </div>
        </div>
      </div>
    </div>
  );
};
